import { useEffect, useRef } from "react";
import { dismissToast, showError, showSucess, showWait } from "../../../services/toast";

type InjectHTMLProps = {
  html: string;
};

export function InjectHTML({ html }: InjectHTMLProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const waitId = showWait("Processando código de embed...");
    let hadError = false;

    try {
      let raw = (html || "").trim();
      
      if (!raw) {
        hadError = true;
        ref.current.innerHTML = "";
        showError("Nenhum código de embed foi informado.");
        return;
      }

      // Converter tag <roteiro> para <script> para executar o código JavaScript
      raw = raw.replace(/<roteiro>([\s\S]*?)<\/roteiro>/gi, '<script>$1</script>');

      // Normalizar quebras de linha
      raw = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      
      const hasScriptOrIframe =
        /<script[\s>]/i.test(raw) || /<iframe[\s>]/i.test(raw);

      if (!hasScriptOrIframe) {
        hadError = true;
        ref.current.innerHTML = "";
        showError(
          "O código informado não parece ser um embed válido.\n" +
            "Verifique se você copiou o embed completo do provedor (geralmente contém <script> ou <iframe>)."
        );
        return;
      }

      const temp = document.createElement("div");
      temp.innerHTML = raw;

      const tempScripts = temp.querySelectorAll("script");

      for (const script of Array.from(tempScripts)) {
        if (!script.src) {
          const code = script.innerHTML;
          const suspiciousChars = /[\u2026\u201C\u201D\u2018\u2019]/;
          if (suspiciousChars.test(code)) {
            hadError = true;
            showError(
              "O código de embed contém caracteres especiais suspeitos (como “aspas tortas” ou '…').\n" +
                "Isso geralmente significa que o embed foi copiado de forma truncada. Copie novamente diretamente da fonte."
            );
            return;
          }
          try {
            new Function(code);
          } catch (e: any) {
            hadError = true;
            const msg =
              e && e.message
                ? `Detalhe técnico: ${e.message}`
                : "Erro de sintaxe no código JS.";
            showError(
              "O código JavaScript do embed parece estar inválido.\n" +
                "Verifique se o embed foi copiado completo (sem cortar a URL, sem '…' e com todas as aspas fechadas).\n\n" +
                msg
            );
            return;
          }
        }
      }
      ref.current.innerHTML = raw;

      const scripts = ref.current.querySelectorAll("script");

      scripts.forEach((oldScript) => {
        const newScript = document.createElement("script");

        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        if (!oldScript.src) {
          newScript.text = oldScript.innerHTML;
        }
        document.body.appendChild(newScript);
      });

      if (!hadError) {
        showSucess("Embed carregado com sucesso.");
      }
    } catch (err) {
      console.error("Erro ao processar código de embed:", err);
      hadError = true;
      showError(
        "Não foi possível processar o código de embed.\n" +
          "Confira o formato e tente colar novamente o código completo."
      );
    } finally {
      dismissToast(waitId as any);
    }
  }, [html]);

  return <div ref={ref} />;
}
